import clsx from 'clsx'
import * as React from 'react'
import { Logger } from 'src/3rds/cube'
import { classToFComponent, FCClassContext } from 'src/3rds/cube-react'

import { makeStyles } from 'tss-react/mui'

import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Switch from '@mui/material/Switch'
import Tab from '@mui/material/Tab'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import AddIcon from '@mui/icons-material/Add'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import PauseIcon from '@mui/icons-material/Pause'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import DownloadIcon from '@mui/icons-material/Download'
import RefreshIcon from '@mui/icons-material/Refresh'
import SaveIcon from '@mui/icons-material/Save'
import SendIcon from '@mui/icons-material/Send'

import { Aba, Bloco, Campanha, CampanhaScope, Entrega, Parceiro } from '../Campanha.scopes'
import { custoEstimado, ROTULO_SITUACAO_TEMPLATE, rotuloDoPublico } from '../Campanha.regras'
import { MAX_CARACTERES_LEGENDA, MAX_CARACTERES_TEXTO } from '../Campanha.service'

const LOG = Logger.get('Campanha-VIEW')

const ROTULO_STATUS: Record<string, string> = {
    enqueued: 'Na fila',
    sent: 'Entregue',
    sent_template: 'Entregue por template',
    discarded_window_expired: 'Janela de 24h expirou',
    discarded_no_interaction: 'Sem interação registrada',
    pending: 'Pendente'
}

const COR_STATUS: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
    enqueued: 'info',
    sent: 'success',
    sent_template: 'success',
    discarded_window_expired: 'warning',
    discarded_no_interaction: 'warning'
}

const rotuloStatus = (status: string) => ROTULO_STATUS[status] ?? status
const corStatus = (status: string) => COR_STATUS[status] ?? 'default'
const formatarDataHora = (data?: Date) => (data ? data.toLocaleString('pt-BR') : '—')
const formatarTamanho = (bytes?: number) => (bytes ? `${(bytes / 1024).toFixed(0)} KB` : '')

const TEM_URL = /https?:\/\//i

/** Só incomoda quando chega perto do limite; passando dele, o campo fica em erro. */
function contador(usados: number, limite: number) {
    if (usados > limite) {
        return `${usados} de ${limite} caracteres — o WhatsApp recusa este envio`
    }
    return usados > limite * 0.8 ? `${usados} de ${limite} caracteres` : ' '
}

const useStyles = makeStyles()((theme) => ({
    view: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        padding: theme.spacing(3),
        rowGap: theme.spacing(3),
        // O body do portal é fixo em 100vh, então o módulo precisa rolar por conta própria.
        overflow: 'auto',
        minHeight: 0,
        // Sem isto o módulo não rola: um Card do MUI tem `overflow: hidden`, e pela regra do
        // flexbox um item com overflow diferente de `visible` tem tamanho mínimo automático ZERO.
        // Os cards então encolhiam para caber na coluna e recortavam o próprio conteúdo — o
        // conteúdo sumia (o botão Salvar inclusive) sem nenhuma barra de rolagem aparecer.
        '& > *': {
            flexShrink: 0
        }
    },
    card: {
        borderRadius: 14,
        boxShadow: '0px 2px 12px rgba(0,0,0,0.05)'
    },
    colunas: {
        display: 'flex',
        gap: theme.spacing(3),
        alignItems: 'flex-start',
        flexWrap: 'wrap'
    },
    colunaEditor: {
        flex: '1 1 520px',
        minWidth: 380
    },
    colunaPreview: {
        flex: '0 1 380px',
        minWidth: 320
    },
    // Fundo do preview: aproxima a leitura do que o parceiro vê no WhatsApp.
    telaWhatsapp: {
        backgroundColor: '#ECE5DD',
        borderRadius: 12,
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        minHeight: 200
    },
    bolha: {
        alignSelf: 'flex-start',
        backgroundColor: '#FFFFFF',
        borderRadius: 10,
        padding: theme.spacing(1, 1.5),
        maxWidth: '85%',
        boxShadow: '0px 1px 1px rgba(0,0,0,0.15)',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontSize: 14,
        lineHeight: 1.45
    },
    bolhaImagem: {
        padding: theme.spacing(0.5),
        '& img': {
            display: 'block',
            maxWidth: '100%',
            borderRadius: 6
        }
    },
    cartaoLink: {
        marginTop: theme.spacing(0.5),
        padding: theme.spacing(0.75, 1),
        borderLeft: `3px solid ${theme.palette.primary.main}`,
        backgroundColor: 'rgba(0,0,0,0.04)',
        borderRadius: 4,
        fontSize: 12,
        color: theme.palette.text.secondary
    },
    bloco: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 10,
        padding: theme.spacing(1.5),
        marginTop: theme.spacing(2)
    },
    blocoCabecalho: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1)
    },
    miniatura: {
        maxWidth: 220,
        maxHeight: 160,
        borderRadius: 8,
        display: 'block',
        marginBottom: theme.spacing(1)
    },
    acoes: {
        display: 'flex',
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
        flexWrap: 'wrap',
        alignItems: 'center'
    },
    chips: {
        display: 'flex',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
        marginTop: theme.spacing(2)
    },
    tabela: {
        marginTop: theme.spacing(2),
        maxHeight: 460
    },
    linhaCampanha: {
        cursor: 'pointer'
    },
    progressoLinha: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        marginTop: theme.spacing(2)
    },
    progressoBarra: {
        flexGrow: 1,
        height: 10,
        borderRadius: 5
    },
    entradaArquivo: {
        display: 'none'
    }
}))

export type CampanhaViewProps = {
    className?: string
    scope: CampanhaScope
}

class CampanhaClass implements FCClassContext<CampanhaViewProps> {
    // :: Fields

    scope!: CampanhaScope
    styles!: ReturnType<typeof useStyles>
    inputArquivo!: React.RefObject<HTMLInputElement>

    // :: emissors

    readonly emitTrocarAba = (_evt: unknown, aba: Aba) => this.scope.onTrocarAba(aba).catch(LOG.caught)
    readonly emitNovaCampanha = () => this.scope.onNovaCampanha().catch(LOG.caught)
    readonly emitAbrirCampanha = (newsId: string) => () => this.scope.onAbrirCampanha(newsId).catch(LOG.caught)

    readonly emitNewsIdChange = (evt: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onNewsIdChange(evt.target.value).catch(LOG.caught)

    readonly emitAdicionarTexto = () => this.scope.onAdicionarTexto().catch(LOG.caught)
    readonly emitEscolherImagem = () => this.inputArquivo.current?.click()

    readonly emitArquivoEscolhido = (evt: React.ChangeEvent<HTMLInputElement>) => {
        const arquivo = evt.target.files?.[0]
        // Zera o input para permitir escolher o mesmo arquivo de novo depois de um erro.
        evt.target.value = ''
        if (arquivo) {
            this.scope.onSelecionarImagem(arquivo).catch(LOG.caught)
        }
    }

    readonly emitTextoBloco = (indice: number) => (evt: React.ChangeEvent<HTMLInputElement>) => {
        const bloco = this.scope.blocos[indice]
        if (bloco?.tipo !== 'texto') return
        this.scope.onAlterarBloco({ indice, bloco: { ...bloco, texto: evt.target.value } }).catch(LOG.caught)
    }

    readonly emitLegendaBloco = (indice: number) => (evt: React.ChangeEvent<HTMLInputElement>) => {
        const bloco = this.scope.blocos[indice]
        if (bloco?.tipo !== 'imagem') return
        this.scope.onAlterarBloco({ indice, bloco: { ...bloco, legenda: evt.target.value } }).catch(LOG.caught)
    }

    readonly emitPreviewLink = (indice: number) => (_evt: unknown, marcado: boolean) => {
        const bloco = this.scope.blocos[indice]
        if (bloco?.tipo !== 'texto') return
        this.scope.onAlterarBloco({ indice, bloco: { ...bloco, previewLink: marcado } }).catch(LOG.caught)
    }

    readonly emitRemoverBloco = (indice: number) => () => this.scope.onRemoverBloco(indice).catch(LOG.caught)

    readonly emitMoverBloco = (indice: number, direcao: -1 | 1) => () =>
        this.scope.onMoverBloco({ indice, direcao }).catch(LOG.caught)

    readonly emitSalvar = () => this.scope.onSalvar().catch(LOG.caught)
    readonly emitIrParaDisparo = () => this.scope.onTrocarAba('disparo').catch(LOG.caught)

    readonly emitSelecionarParceiro = (_evt: unknown, parceiro: Parceiro | null) =>
        this.scope.onSelecionarParceiro(parceiro ?? undefined).catch(LOG.caught)

    readonly emitBuscarParceiro = (_evt: unknown, texto: string, motivo: string) => {
        if (motivo === 'input') {
            this.scope.onBuscarParceiro(texto).catch(LOG.caught)
        }
    }

    readonly emitEnviarTeste = () => this.scope.onEnviarTeste().catch(LOG.caught)
    readonly emitAbrirConfirmacao = () => this.scope.onAbrirConfirmacao().catch(LOG.caught)
    readonly emitFecharConfirmacao = () => this.scope.onFecharConfirmacao().catch(LOG.caught)
    readonly emitDisparar = () => this.scope.onDisparar().catch(LOG.caught)
    readonly emitPausar = () => this.scope.onPausar().catch(LOG.caught)
    readonly emitCriarTemplate = () => this.scope.onCriarTemplate().catch(LOG.caught)
    readonly emitEscolherAlcance = (_: React.ChangeEvent<HTMLInputElement>, valor: string) =>
        this.scope.onTemplateForaDaJanela(valor === 'todos').catch(LOG.caught)
    readonly emitPublicoChange = (evt: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onPublicoChange(evt.target.value).catch(LOG.caught)
    readonly emitAbrirExclusao = (newsId: string) => (evt: React.MouseEvent) => {
        // A linha abre a campanha; o botão de excluir não.
        evt.stopPropagation()
        this.scope.onAbrirExclusao(newsId).catch(LOG.caught)
    }
    readonly emitFecharExclusao = () => this.scope.onFecharExclusao().catch(LOG.caught)
    readonly emitExcluirTemplatesChange = (_: React.ChangeEvent<HTMLInputElement>, marcado: boolean) =>
        this.scope.onExcluirTemplatesChange(marcado).catch(LOG.caught)
    readonly emitConfirmarExclusao = () => this.scope.onConfirmarExclusao().catch(LOG.caught)
    readonly emitAtualizarTemplate = () => this.scope.onAtualizarTemplate().catch(LOG.caught)
    readonly emitRetomar = () => this.scope.onRetomar().catch(LOG.caught)
    readonly emitAtualizarSituacao = () => this.scope.onAtualizarSituacao().catch(LOG.caught)
    readonly emitBaixarRelatorio = () => this.scope.onBaixarRelatorio().catch(LOG.caught)

    // :: Methods

    onSyncState({ scope }: CampanhaViewProps) {
        this.scope = scope
        this.styles = useStyles()
        this.inputArquivo = React.useRef<HTMLInputElement>(null)
    }

    render({ className }: CampanhaViewProps) {
        const { classes } = this.styles
        const { scope } = this

        return (
            <div className={clsx(classes.view, className)}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        Campanhas no WhatsApp
                    </Typography>
                    <Typography color="text.secondary" fontSize={15}>
                        Mensagens enviadas a todos os parceiros pela LIA. Quem falou com a LIA nas últimas 24 horas
                        recebe na hora, sem custo. Os demais recebem quando voltarem a interagir ou, se a campanha
                        enviar template, pelo template, que é pago.
                    </Typography>
                </Box>

                {this.renderLista()}

                <Card className={classes.card}>
                    <Tabs value={scope.aba} onChange={this.emitTrocarAba}>
                        <Tab value="editor" label="Conteúdo" />
                        <Tab value="disparo" label="Testar e disparar" disabled={!scope.campanha} />
                        <Tab value="situacao" label="Situação" disabled={!scope.campanha} />
                    </Tabs>
                    <Divider />
                    <CardContent>
                        {scope.aba === 'editor' && this.renderEditor()}
                        {scope.aba === 'disparo' && this.renderDisparo()}
                        {scope.aba === 'situacao' && this.renderSituacao()}
                    </CardContent>
                </Card>

                {this.renderConfirmacao()}
                {this.renderExclusao()}
            </div>
        )
    }

    // :: Lista

    renderLista() {
        const { scope } = this
        const { classes } = this.styles

        return (
            <Card className={classes.card}>
                <CardContent>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="h6" fontWeight={600} flexGrow={1}>
                            Campanhas
                        </Typography>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={this.emitNovaCampanha}>
                            Nova campanha
                        </Button>
                    </Box>

                    {scope.carregandoLista && <LinearProgress sx={{ mt: 2 }} />}

                    {!scope.carregandoLista && scope.campanhas.length === 0 && (
                        <Typography color="text.secondary" mt={2}>
                            Nenhuma campanha criada ainda.
                        </Typography>
                    )}

                    {scope.campanhas.length > 0 && (
                        <TableContainer className={classes.tabela}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Campanha</TableCell>
                                        <TableCell>Situação</TableCell>
                                        <TableCell align="right">Entregues</TableCell>
                                        <TableCell align="right">Na fila</TableCell>
                                        <TableCell>Criada em</TableCell>
                                        <TableCell padding="checkbox" />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {scope.campanhas.map((item) => (
                                        <TableRow
                                            key={item.newsId}
                                            hover
                                            className={classes.linhaCampanha}
                                            selected={item.newsId === scope.campanha?.newsId}
                                            onClick={this.emitAbrirCampanha(item.newsId)}
                                        >
                                            <TableCell>{item.newsId}</TableCell>
                                            <TableCell>{this.renderSituacaoCampanha(item)}</TableCell>
                                            <TableCell align="right">{item.enviados}</TableCell>
                                            <TableCell align="right">{item.enfileirados}</TableCell>
                                            <TableCell>{formatarDataHora(item.criadaEm)}</TableCell>
                                            <TableCell padding="checkbox">
                                                <Tooltip title="Excluir a campanha">
                                                    <IconButton
                                                        size="small"
                                                        aria-label={`Excluir ${item.newsId}`}
                                                        onClick={this.emitAbrirExclusao(item.newsId)}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>
        )
    }

    renderSituacaoCampanha(item: Campanha) {
        if (!item.ativa) {
            return <Chip size="small" label="Pausada" color="warning" />
        }
        if (!item.disparadaEm) {
            return <Chip size="small" label="Rascunho" />
        }
        if (item.concluida) {
            return <Chip size="small" label="Concluída" color="success" />
        }
        return <Chip size="small" label="Entregando" color="info" />
    }

    // :: Editor

    renderEditor() {
        const { scope } = this
        const { classes } = this.styles

        return (
            <Box className={classes.colunas}>
                <Box className={classes.colunaEditor}>
                    <TextField
                        label="Identificador da campanha"
                        size="small"
                        fullWidth
                        value={scope.newsId}
                        onChange={this.emitNewsIdChange}
                        disabled={!!scope.campanha}
                        placeholder="convite-webinar-2026-09"
                        helperText={
                            scope.campanha
                                ? 'O identificador não muda depois de criada (é a chave de quem já recebeu).'
                                : 'Letras, números e hífen. É por ele que o sistema controla quem já recebeu.'
                        }
                    />

                    {scope.blocos.map((bloco, indice) => this.renderBlocoEditor(bloco, indice))}

                    {scope.erroFormulario && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            {scope.erroFormulario}
                        </Alert>
                    )}

                    <input
                        ref={this.inputArquivo}
                        type="file"
                        accept="image/jpeg,image/png"
                        className={classes.entradaArquivo}
                        onChange={this.emitArquivoEscolhido}
                    />

                    <Box className={classes.acoes}>
                        <Button startIcon={<AddIcon />} onClick={this.emitAdicionarTexto}>
                            Mensagem de texto
                        </Button>
                        <Button
                            startIcon={scope.enviandoImagem ? <CircularProgress size={16} /> : <ImageOutlinedIcon />}
                            onClick={this.emitEscolherImagem}
                            disabled={scope.enviandoImagem}
                        >
                            Imagem
                        </Button>
                        <Box flexGrow={1} />
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={this.emitSalvar}
                            disabled={scope.salvando}
                        >
                            Salvar
                        </Button>
                    </Box>

                    {scope.alterado && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            Há alterações não salvas. Salvar não envia nada — o envio fica na aba{' '}
                            <b>Testar e disparar</b>.
                        </Alert>
                    )}

                    {/* Sem isto, as abas de envio ficam cinza e ninguém descobre por quê. */}
                    {!scope.campanha ? (
                        <Typography variant="body2" color="text.secondary" mt={2}>
                            Salve a campanha para liberar o envio de teste e o disparo.
                        </Typography>
                    ) : (
                        <Box className={classes.acoes}>
                            <Button
                                variant="outlined"
                                endIcon={<SendIcon />}
                                onClick={this.emitIrParaDisparo}
                                disabled={scope.alterado}
                            >
                                Ir para testar e disparar
                            </Button>
                            {scope.alterado && (
                                <Typography variant="body2" color="text.secondary">
                                    Salve as alterações primeiro.
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>

                <Box className={classes.colunaPreview}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Como o parceiro vai receber
                    </Typography>
                    <Box className={classes.telaWhatsapp}>
                        {scope.blocos.length === 0 && (
                            <Typography color="text.secondary" fontSize={14}>
                                Adicione uma mensagem para ver o preview.
                            </Typography>
                        )}
                        {scope.blocos.map((bloco, indice) => this.renderBolha(bloco, indice))}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        Cada bloco chega como uma mensagem separada, na ordem acima.
                    </Typography>
                </Box>
            </Box>
        )
    }

    renderBlocoEditor(bloco: Bloco, indice: number) {
        const { scope } = this
        const { classes } = this.styles
        const ultimo = indice === scope.blocos.length - 1

        return (
            <Box key={indice} className={classes.bloco}>
                <Box className={classes.blocoCabecalho}>
                    <Chip
                        size="small"
                        label={bloco.tipo === 'imagem' ? `${indice + 1}. Imagem` : `${indice + 1}. Texto`}
                        color={bloco.tipo === 'imagem' ? 'secondary' : 'default'}
                    />
                    <Box flexGrow={1} />
                    <Tooltip title="Subir">
                        <span>
                            <IconButton size="small" onClick={this.emitMoverBloco(indice, -1)} disabled={indice === 0}>
                                <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Descer">
                        <span>
                            <IconButton size="small" onClick={this.emitMoverBloco(indice, 1)} disabled={ultimo}>
                                <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Remover">
                        <IconButton size="small" onClick={this.emitRemoverBloco(indice)}>
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>

                {bloco.tipo === 'imagem' ? (
                    <>
                        {bloco.previewUrl && <img src={bloco.previewUrl} alt="" className={classes.miniatura} />}
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            {bloco.arquivo} {formatarTamanho(bloco.byteSize)}
                        </Typography>
                        <TextField
                            label="Legenda (opcional)"
                            size="small"
                            fullWidth
                            value={bloco.legenda ?? ''}
                            onChange={this.emitLegendaBloco(indice)}
                            error={(bloco.legenda?.length ?? 0) > MAX_CARACTERES_LEGENDA}
                            helperText={contador(bloco.legenda?.length ?? 0, MAX_CARACTERES_LEGENDA)}
                        />
                    </>
                ) : (
                    <>
                        <TextField
                            label="Mensagem"
                            multiline
                            minRows={4}
                            // Alto o bastante para uma mensagem longa aparecer inteira: quem rola
                            // é o módulo, não um campo dentro de outro campo.
                            maxRows={40}
                            fullWidth
                            value={bloco.texto}
                            onChange={this.emitTextoBloco(indice)}
                            error={bloco.texto.length > MAX_CARACTERES_TEXTO}
                            helperText={contador(bloco.texto.length, MAX_CARACTERES_TEXTO)}
                        />
                        {TEM_URL.test(bloco.texto) && (
                            <FormControlLabel
                                sx={{ mt: 1 }}
                                control={
                                    <Switch
                                        size="small"
                                        checked={bloco.previewLink ?? true}
                                        onChange={this.emitPreviewLink(indice)}
                                    />
                                }
                                label={
                                    <Typography variant="body2" color="text.secondary">
                                        Mostrar o cartão de preview do link
                                    </Typography>
                                }
                            />
                        )}
                    </>
                )}
            </Box>
        )
    }

    renderBolha(bloco: Bloco, indice: number) {
        const { classes } = this.styles

        if (bloco.tipo === 'imagem') {
            return (
                <Box key={indice} className={clsx(classes.bolha, classes.bolhaImagem)}>
                    {bloco.previewUrl ? (
                        <img src={bloco.previewUrl} alt="" />
                    ) : (
                        <Typography fontSize={14}>📷 {bloco.arquivo ?? 'Imagem'}</Typography>
                    )}
                    {bloco.legenda && (
                        <Typography fontSize={14} px={1} pt={0.5}>
                            {bloco.legenda}
                        </Typography>
                    )}
                </Box>
            )
        }

        const mostraCartao = TEM_URL.test(bloco.texto) && (bloco.previewLink ?? true)

        return (
            <Box key={indice} className={classes.bolha}>
                {bloco.texto || <span style={{ opacity: 0.5 }}>(mensagem vazia)</span>}
                {mostraCartao && <Box className={classes.cartaoLink}>Prévia do link aparece aqui</Box>}
            </Box>
        )
    }

    // :: Disparo

    renderDisparo() {
        const { scope } = this
        const { classes } = this.styles
        const campanha = scope.campanha
        if (!campanha) return null

        const alcance = scope.alcance

        return (
            <Box>
                {!campanha.ativa && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Campanha pausada — ninguém recebe enquanto ela estiver assim.
                    </Alert>
                )}

                <Typography variant="h6" fontWeight={600}>
                    Alcance agora
                </Typography>
                {scope.carregandoAlcance && <LinearProgress sx={{ mt: 1 }} />}
                <Box className={classes.chips}>
                    <Chip label={`Parceiros: ${alcance?.parceirosTotal ?? '—'}`} />
                    <Chip label={`Com telefone: ${alcance?.comPhone1 ?? '—'}`} />
                    <Chip label={`Janela de 24h aberta: ${alcance?.janelaAberta ?? '—'}`} color="info" />
                    <Chip
                        label={`Recebem agora: ${alcance?.alcancaveisAgora ?? '—'}`}
                        color="primary"
                        variant="filled"
                    />
                    <Chip label={`Já receberam: ${alcance?.jaEnviados ?? 0}`} color="success" variant="outlined" />
                    <Chip
                        label={`Fora da janela: ${alcance?.foraDaJanela ?? '—'}`}
                        color={alcance?.templateNoDisparo ? 'warning' : 'default'}
                        variant="outlined"
                    />
                </Box>
                <Typography variant="caption" color="text.secondary">
                    {alcance?.templateNoDisparo
                        ? 'Quem está fora da janela de 24h recebe o template da campanha, que é pago. Quem está com a ' +
                          'janela aberta recebe as mensagens como texto, sem custo.'
                        : 'Quem está fora da janela de 24h recebe depois, automaticamente, na primeira vez que falar ' +
                          'com a LIA.'}
                </Typography>

                <Divider sx={{ my: 3 }} />

                {this.renderTemplate()}

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" fontWeight={600}>
                    1. Teste com um parceiro
                </Typography>
                <Typography color="text.secondary" fontSize={14}>
                    Obrigatório antes do disparo geral: a mensagem enviada não pode ser desfeita.
                </Typography>

                <Box className={classes.acoes}>
                    <Autocomplete
                        sx={{ minWidth: 380, flexGrow: 1 }}
                        options={scope.parceiros}
                        value={scope.parceiroTeste ?? null}
                        onChange={this.emitSelecionarParceiro}
                        onInputChange={this.emitBuscarParceiro}
                        // A lista já vem filtrada pelo servidor (são milhares de parceiros).
                        filterOptions={(opcoes) => opcoes}
                        loading={scope.carregandoParceiros}
                        loadingText="Buscando parceiros…"
                        noOptionsText="Nenhum parceiro encontrado"
                        getOptionLabel={(parceiro) => `${parceiro.nome} — ${parceiro.email}`}
                        isOptionEqualToValue={(a, b) => a.ownerId === b.ownerId}
                        renderInput={(params) => <TextField {...params} label="Parceiro para o teste" size="small" />}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<SendIcon />}
                        onClick={this.emitEnviarTeste}
                        disabled={
                            !scope.parceiroTeste || scope.enviandoTeste || scope.diagnostico?.podeReceberAgora === false
                        }
                    >
                        Enviar teste
                    </Button>
                </Box>

                {scope.carregandoDiagnostico && <LinearProgress sx={{ mt: 2 }} />}
                {this.renderDiagnostico()}

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" fontWeight={600}>
                    2. Disparar para todos
                </Typography>

                {campanha.testada ? (
                    <Alert severity="success" sx={{ mt: 1 }}>
                        Teste entregue em {formatarDataHora(campanha.testadaEm)} — o conteúdo atual já foi conferido.
                    </Alert>
                ) : scope.aguardandoTeste ? (
                    // A entrega acontece na fila, um instante depois do envio: sem este aviso a
                    // tela parece travada segurando o disparo.
                    <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mt: 1 }}>
                        Teste enfileirado — aguardando a confirmação de entrega para liberar o disparo.
                    </Alert>
                ) : (
                    <Alert severity="info" sx={{ mt: 1 }}>
                        Envie um teste do conteúdo atual para liberar o disparo geral.
                    </Alert>
                )}

                {campanha.template.impedimentoDoDisparo && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        {campanha.template.impedimentoDoDisparo}
                    </Alert>
                )}

                <Box className={classes.acoes}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SendIcon />}
                        onClick={this.emitAbrirConfirmacao}
                        disabled={
                            !campanha.testada ||
                            !campanha.ativa ||
                            scope.disparando ||
                            Boolean(campanha.template.impedimentoDoDisparo)
                        }
                    >
                        Disparar para todos
                    </Button>

                    {campanha.ativa ? (
                        <Button startIcon={<PauseIcon />} onClick={this.emitPausar}>
                            Pausar
                        </Button>
                    ) : (
                        <Button startIcon={<PlayArrowIcon />} onClick={this.emitRetomar}>
                            Reativar
                        </Button>
                    )}
                </Box>

                {campanha.disparadaEm && (
                    <Typography variant="caption" color="text.secondary">
                        Disparada em {formatarDataHora(campanha.disparadaEm)}. Enquanto estiver ativa e não concluída, a
                        LIA continua entregando a quem for voltando a interagir.
                    </Typography>
                )}
            </Box>
        )
    }

    /** O template da campanha para quem está fora da janela de 24 h (E29). */
    /** Quem recebe: só a janela aberta (sem custo) ou todos, com o template para quem está fora (E29). */
    renderTemplate() {
        const { scope } = this
        const { classes } = this.styles
        const campanha = scope.campanha
        if (!campanha) return null
        const t = campanha.template
        const rotulo = ROTULO_SITUACAO_TEMPLATE[t.situacao]
        const foraDaJanela = scope.alcance?.foraDaJanela ?? 0
        const bloqueado = !scope.podeGerirTemplate || scope.salvandoTemplate

        return (
            <Box>
                <Typography variant="h6" fontWeight={600}>
                    Quem recebe
                </Typography>
                <RadioGroup value={t.foraDaJanela ? 'todos' : 'janela'} onChange={this.emitEscolherAlcance}>
                    <FormControlLabel
                        value="janela"
                        disabled={bloqueado}
                        control={<Radio />}
                        label={
                            <Box>
                                <Typography>Só quem está com a janela de 24 h aberta</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Sem custo. Quem está fora da janela recebe quando voltar a falar com a LIA, enquanto
                                    a campanha estiver ativa.
                                </Typography>
                            </Box>
                        }
                    />
                    <FormControlLabel
                        value="todos"
                        disabled={bloqueado || (!t.foraDaJanela && t.problemas.length > 0)}
                        control={<Radio />}
                        label={
                            <Box>
                                <Typography>Também quem está fora da janela, pelo template da campanha</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Pago como marketing: {foraDaJanela} parceiros fora da janela no público escolhido,{' '}
                                    {custoEstimado(foraDaJanela)}. O template tem a mesma imagem e o mesmo texto e passa
                                    pela aprovação da Meta. Quem está com a janela aberta recebe como texto, sem custo.
                                </Typography>
                            </Box>
                        }
                    />
                </RadioGroup>
                {!scope.podeGerirTemplate && (
                    <Typography variant="caption" color="text.secondary">
                        A escolha e o template são com um administrador.
                    </Typography>
                )}
                {!t.foraDaJanela && t.problemas.length > 0 && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                        Para mandar a quem está fora da janela, o conteúdo precisa caber num template:
                        <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                            {t.problemas.map((p) => (
                                <li key={p}>{p}</li>
                            ))}
                        </ul>
                    </Alert>
                )}

                {t.foraDaJanela && (
                    <Box sx={{ pl: 4 }}>
                        <TextField
                            select
                            size="small"
                            label="Mandar o template para"
                            value={t.publicoDias ? `${t.publicoDias}` : ''}
                            onChange={this.emitPublicoChange}
                            disabled={bloqueado}
                            sx={{ mt: 2, minWidth: 420, maxWidth: '100%' }}
                            SelectProps={{ displayEmpty: true }}
                            InputLabelProps={{ shrink: true }}
                            helperText="Atuou = conversou com a LIA, consultou CNPJ ou criou negócio no período."
                        >
                            {(
                                scope.alcance?.foraDaJanelaPorPublico ?? [{ dias: t.publicoDias ?? null, parceiros: 0 }]
                            ).map((p) => (
                                <MenuItem key={p.dias ?? 'todos'} value={p.dias ? `${p.dias}` : ''}>
                                    {rotuloDoPublico(p.dias)}: {p.parceiros} parceiros, {custoEstimado(p.parceiros)}
                                </MenuItem>
                            ))}
                        </TextField>
                        <Box className={classes.chips} alignItems="center">
                            <Typography variant="body2">Template:</Typography>
                            <Chip label={rotulo.rotulo} color={rotulo.cor} />
                            {t.nome && (
                                <Typography variant="body2" color="text.secondary">
                                    {t.nome}
                                    {t.criadoEm ? ` · criado em ${formatarDataHora(new Date(t.criadoEm))}` : ''}
                                </Typography>
                            )}
                            {t.nome && (
                                <Button size="small" startIcon={<RefreshIcon />} onClick={this.emitAtualizarTemplate}>
                                    Atualizar
                                </Button>
                            )}
                        </Box>
                        {t.motivoRecusa && t.situacao === 'recusado' && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                Motivo da Meta: {t.motivoRecusa}
                            </Alert>
                        )}
                        {t.problemas.length > 0 && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                O conteúdo não cabe mais num template:
                                <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                                    {t.problemas.map((p) => (
                                        <li key={p}>{p}</li>
                                    ))}
                                </ul>
                            </Alert>
                        )}
                        {t.situacao === 'desatualizado' && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                A campanha mudou depois que o template foi criado. Crie de novo para voltar a alcançar
                                quem está fora da janela.
                            </Alert>
                        )}
                        {scope.podeGerirTemplate && t.podeCriar && (
                            <Box className={classes.acoes}>
                                <Button
                                    variant="outlined"
                                    onClick={this.emitCriarTemplate}
                                    disabled={scope.salvandoTemplate || scope.alterado}
                                    startIcon={scope.salvandoTemplate ? <CircularProgress size={16} /> : undefined}
                                >
                                    {t.situacao === 'sem_template' ? 'Criar template da campanha' : 'Criar de novo'}
                                </Button>
                                {scope.alterado && (
                                    <Typography variant="caption" color="text.secondary">
                                        Salve a campanha antes de criar o template.
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        )
    }

    renderDiagnostico() {
        const { scope } = this
        const diagnostico = scope.diagnostico
        if (!diagnostico) return null

        if (diagnostico.recebeTemplate) {
            return (
                <Alert severity="info" sx={{ mt: 2 }}>
                    {diagnostico.nome} está fora da janela de 24h e recebe o template da campanha (envio pago).
                </Alert>
            )
        }

        if (diagnostico.podeReceberAgora) {
            return (
                <Alert severity="success" sx={{ mt: 2 }}>
                    {diagnostico.nome} pode receber agora (última interação em{' '}
                    {formatarDataHora(diagnostico.ultimaInteracaoEm)}).
                </Alert>
            )
        }

        return (
            <Alert severity="warning" sx={{ mt: 2 }}>
                Esse parceiro não pode receber agora:
                <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                    {diagnostico.impedimentos.map((item) => (
                        <li key={item}>{item}</li>
                    ))}
                </ul>
            </Alert>
        )
    }

    // :: Situação

    renderSituacao() {
        const { scope } = this
        const { classes } = this.styles
        const situacao = scope.situacao
        const campanha = scope.campanha
        if (!campanha) return null

        // Uma campanha não tem total fixo: o catch-up vai trazendo parceiros conforme eles
        // voltam a interagir. O denominador honesto é quantos a campanha JÁ alcançou (todas as
        // entregas registradas, inclusive as descartadas — que voltam à fila numa próxima
        // interação). Antes usávamos entregues+na fila, que mudava a cada leva e dava a
        // impressão de um total errado.
        const entregues = (situacao?.porStatus.sent ?? 0) + (situacao?.porStatus.sent_template ?? 0)
        const naFila = situacao?.porStatus.enqueued ?? 0
        const alcancados = Object.values(situacao?.porStatus ?? {}).reduce((soma, qtde) => soma + qtde, 0)
        const progresso = alcancados > 0 ? Math.round((entregues / alcancados) * 100) : 0

        return (
            <Box>
                <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="h6" fontWeight={600} flexGrow={1}>
                        Entrega
                    </Typography>
                    <Button
                        startIcon={<RefreshIcon />}
                        onClick={this.emitAtualizarSituacao}
                        disabled={scope.carregandoSituacao}
                    >
                        Atualizar
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={scope.baixandoRelatorio ? <CircularProgress size={16} /> : <DownloadIcon />}
                        onClick={this.emitBaixarRelatorio}
                        disabled={scope.baixandoRelatorio || (situacao?.entregas.length ?? 0) === 0}
                    >
                        Baixar relatório
                    </Button>
                </Box>

                {!campanha.disparadaEm && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        Campanha ainda não disparada. Os números abaixo consideram só os testes enviados.
                    </Alert>
                )}

                <Box className={classes.progressoLinha}>
                    <LinearProgress variant="determinate" value={progresso} className={classes.progressoBarra} />
                    <Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
                        {entregues} entregues de {alcancados} alcançados ({progresso}%)
                    </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                    {campanha.concluida
                        ? 'Todos os parceiros alcançáveis já receberam.'
                        : `Este total cresce ao longo do tempo: só entra na conta quem já foi alcançado pela ` +
                          (campanha.template.enviando
                              ? `campanha. Quem está fora da janela de 24h recebe o template`
                              : `campanha. Quem está fora da janela de 24h entra quando voltar a falar com a LIA`) +
                          (naFila > 0 ? ` — ${naFila} na fila agora.` : '.')}
                </Typography>

                <Box className={classes.chips}>
                    {Object.entries(situacao?.porStatus ?? {}).map(([status, qtde]) => (
                        <Chip
                            key={status}
                            size="small"
                            label={`${rotuloStatus(status)}: ${qtde}`}
                            color={corStatus(status)}
                        />
                    ))}
                    {campanha.concluida && <Chip size="small" color="success" label="Todos alcançados" />}
                </Box>

                {(situacao?.erros400 ?? 0) > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        {situacao?.erros400} erro(s) 400 do WhatsApp acumulados no servidor. Normalmente é janela de 24h
                        expirada ou número inválido.
                    </Alert>
                )}

                <TableContainer className={classes.tabela}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Parceiro</TableCell>
                                <TableCell>Situação</TableCell>
                                <TableCell>Entregue em</TableCell>
                                <TableCell>Última interação</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(situacao?.entregas ?? []).map((entrega: Entrega) => (
                                <TableRow key={entrega.ownerId} hover>
                                    <TableCell>{entrega.nome ?? entrega.ownerId}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={rotuloStatus(entrega.status)}
                                            color={corStatus(entrega.status)}
                                        />
                                    </TableCell>
                                    <TableCell>{formatarDataHora(entrega.enviadaEm)}</TableCell>
                                    <TableCell>{formatarDataHora(entrega.ultimaInteracaoEm)}</TableCell>
                                </TableRow>
                            ))}
                            {(situacao?.entregas.length ?? 0) === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4}>
                                        <Typography color="text.secondary">
                                            Nenhuma entrega registrada ainda.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        )
    }

    // :: Confirmação

    renderConfirmacao() {
        const { scope } = this
        const alcance = scope.alcance

        return (
            <Dialog open={scope.confirmacaoAberta} onClose={this.emitFecharConfirmacao} maxWidth="sm" fullWidth>
                <DialogTitle>Disparar a campanha para todos?</DialogTitle>
                <DialogContent>
                    {alcance?.templateNoDisparo ? (
                        <Typography>
                            <b>{alcance.alcancaveisAgora} parceiros</b> com a janela aberta recebem as mensagens como
                            texto, sem custo. <b>{alcance.foraDaJanela} parceiros</b> fora da janela (
                            {rotuloDoPublico(scope.campanha?.template.publicoDias).toLowerCase()}) recebem o template da
                            campanha, pago como marketing: {custoEstimado(alcance.foraDaJanela)} pela tabela da Meta.
                        </Typography>
                    ) : (
                        <Typography>
                            <b>{alcance?.alcancaveisAgora ?? 0} parceiros</b> recebem imediatamente. Os demais recebem
                            assim que voltarem a falar com a LIA, enquanto a campanha estiver ativa.
                        </Typography>
                    )}

                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Mensagem enviada no WhatsApp não pode ser desfeita. Para interromper o restante, use Pausar.
                    </Alert>

                    {(alcance?.jaEnviados ?? 0) > 0 && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            {alcance?.jaEnviados} parceiro(s) já receberam esta campanha e não recebem de novo.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.emitFecharConfirmacao} disabled={scope.disparando}>
                        Cancelar
                    </Button>
                    <Button variant="contained" onClick={this.emitDisparar} disabled={scope.disparando}>
                        Disparar
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    // :: Exclusão

    renderExclusao() {
        const { scope } = this
        const previa = scope.exclusao
        const excluiveis = previa?.templates.filter((t) => t.excluivel) ?? []
        const presos = previa?.templates.filter((t) => !t.excluivel) ?? []

        return (
            <Dialog open={Boolean(previa)} onClose={this.emitFecharExclusao} maxWidth="sm" fullWidth>
                {previa && (
                    <>
                        <DialogTitle>Excluir a campanha {previa.newsId}?</DialogTitle>
                        <DialogContent>
                            {previa.impedimento ? (
                                <Alert severity="warning">{previa.impedimento}</Alert>
                            ) : (
                                <Typography>
                                    A campanha sai do portal com o registro de {previa.entregas} entrega(s) e o
                                    relatório. As mensagens já entregues continuam no WhatsApp dos parceiros e no
                                    histórico das conversas.
                                </Typography>
                            )}
                            {!previa.impedimento && excluiveis.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    {scope.podeGerirTemplate ? (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={scope.excluirTemplates}
                                                    onChange={this.emitExcluirTemplatesChange}
                                                />
                                            }
                                            label={`Excluir também na Meta o(s) template(s) da campanha: ${excluiveis
                                                .map((t) => t.nome)
                                                .join(', ')}`}
                                        />
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">
                                            Os templates da campanha ({excluiveis.map((t) => t.nome).join(', ')}) ficam
                                            na conta; um administrador pode excluí-los na tela de templates.
                                        </Typography>
                                    )}
                                    {scope.excluirTemplates && excluiveis.some((t) => t.status === 'APPROVED') && (
                                        <Alert severity="warning" sx={{ mt: 1 }}>
                                            A Meta não deixa criar outro template com o nome de um aprovado excluído por
                                            um tempo (cerca de 30 dias).
                                        </Alert>
                                    )}
                                </Box>
                            )}
                            {!previa.impedimento && presos.length > 0 && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    Ficam na conta: {presos.map((t) => `${t.nome} (${t.motivo})`).join('; ')}.
                                </Alert>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={this.emitFecharExclusao} disabled={scope.excluindo}>
                                Cancelar
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={this.emitConfirmarExclusao}
                                disabled={scope.excluindo || Boolean(previa.impedimento)}
                            >
                                Excluir
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        )
    }
}

export const CampanhaView = classToFComponent(CampanhaClass, React)
export default CampanhaView
